<template>
  <div id="auth-ui-register-container">
    <section class="section auth-ui-section--is-narrow">
      <div class="container auth-ui-container--has-centered-content">
        <v-card :classes="'auth-ui-card--is-not-full-width'">
          <div slot="header-title">
            {{ $t("auth.registerTitle") }} {{ title }}
          </div>
          <form slot="body">
            <div class="field">
              <label class="label auth-ui-label">{{
                $t("auth.registerEmailLabel")
              }}</label>
              <div
                class="control has-icons-left has-icons-right auth-ui-control-has-icons-left"
              >
                <input
                  v-model="email"
                  :class="{ 'is-danger': errors.email }"
                  :placeholder="$t('auth.registerEmailPlaceholder')"
                  class="input auth-ui-input"
                  type="email"
                  autocomplete="email"
                  @blur="validateEmail()"
                  @focus="clearErrorFromField('email')"
                >
                <span class="icon is-small is-left auth-ui-icon">
                  <i class="fa fa-envelope" />
                </span>
                <span class="icon is-small is-right">
                  <i
                    :class="{ 'fa-exclamation-triangle': errors.email }"
                    class="fa"
                  />
                </span>
              </div>
              <p
                :class="{
                  'is-danger': errors.email,
                  'auth-ui-error--is-hidden': !errors.email,
                }"
                class="help"
              >
                {{ $t("auth.registerEmailValidationError") }}
              </p>
            </div>

            <div class="field">
              <label class="label auth-ui-label">{{
                $t("auth.registerPasswordLabel")
              }}</label>
              <div
                class="control has-icons-left has-icons-right auth-ui-control-has-icons-left"
              >
                <input
                  v-model="password"
                  :class="{ 'is-danger': errors.passwordStrength }"
                  :placeholder="$t('auth.registerPasswordPlaceholder')"
                  class="input auth-ui-input"
                  type="password"
                  autocomplete="new-password"
                  @blur="validatePasswordStrength()"
                  @focus="clearErrorFromField('passwordStrength')"
                >
                <span class="icon is-small is-left auth-ui-icon">
                  <i class="fa fa-lock" />
                </span>
                <span class="icon is-small is-right">
                  <i
                    :class="{
                      'fa-exclamation-triangle': errors.passwordStrength,
                    }"
                    class="fa"
                  />
                </span>
              </div>
              <p
                :class="{
                  'is-danger': errors.passwordStrength,
                  'auth-ui-error--is-hidden': !errors.passwordStrength,
                }"
                class="help"
              >
                <!-- eslint-disable vue/no-v-html -->
                <span
                  v-html="
                    $t('auth.registerPasswordStrengthError', {
                      link: `<a href='#password-requirements'>
                            ${$t(
                              'auth.registerPasswordStrengthErrorLinkText'
                      )}</a>`,
                    })
                  "
                />
                <!-- eslint-enable vue/no-v-html -->
              </p>
            </div>

            <div class="field">
              <label class="label auth-ui-label">{{
                $t("auth.registerPasswordLabel2")
              }}</label>
              <div
                class="control has-icons-left has-icons-right auth-ui-control-has-icons-left"
              >
                <input
                  v-model="password2"
                  :class="{ 'is-danger': errors.passwordsNotMatching }"
                  :placeholder="$t('auth.registerPasswordPlaceholder2')"
                  class="input auth-ui-input"
                  type="password"
                  autocomplete="new-password"
                  @blur="validatePasswordsMatching()"
                  @focus="clearErrorFromField('passwordsNotMatching')"
                >
                <span class="icon is-small is-left auth-ui-icon">
                  <i class="fa fa-lock" />
                </span>
                <span class="icon is-small is-right">
                  <i
                    :class="{
                      'fa-exclamation-triangle': errors.passwordsNotMatching,
                    }"
                    class="fa"
                  />
                </span>
              </div>
              <p
                :class="{
                  'is-danger': errors.passwordsNotMatching,
                  'auth-ui-error--is-hidden': !errors.passwordsNotMatching,
                }"
                class="help"
              >
                {{ $t("auth.registerPasswordMatchingError") }}
              </p>
            </div>

            <v-password-requirements />

            <div class="field">
              <div class="control">
                <div
                  class="auth-ui-blockcheckbox-checkbox auth-ui-checkbox-container"
                >
                  <input
                    id="auth-ui-blockcheckbox"
                    v-model="terms"
                    type="checkbox"
                    class="auth-ui-blockcheckbox"
                  >
                  <label
                    class="auth-ui-blockcheckbox-label"
                    for="auth-ui-blockcheckbox"
                  >
                    <span>
                      {{ $t("auth.registerTermsLabel") }}
                      <a
                        :href="termsUrl"
                        target="_blank"
                        class="auth-ui-checkbox-link"
                      >{{ $t("auth.registerTermsLink") }}</a>.
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <article
              v-if="serverResponse.visible"
              class="auth-ui-card__message-container"
            >
              <div
                :class="{
                  'is-danger': serverResponse.error,
                  'is-success': !serverResponse.error,
                }"
                class="message"
              >
                <div class="message-body">
                  <button
                    class="delete auth-ui-delete--is-high"
                    aria-label="delete"
                    @click="showMessage(false)"
                  />
                  <!-- TODO figure out a better way to show the response? -->
                  <!-- eslint-disable vue/no-v-html -->
                  <span v-html="serverResponse.message" />
                  <!-- eslint-enable vue/no-v-html -->
                </div>
              </div>
              <div class="auth-ui-card-separator" />
            </article>

            <div class="field is-grouped is-grouped-centered">
              <div class="control auth-ui-button-and-spinner-container">
                <button
                  :disabled="registerButtonIsDisabled"
                  class="button is-link auth-ui-button"
                  @click.prevent="register()"
                >
                  {{ $t("auth.registerButton") }}
                </button>
              </div>
            </div>

            <div class="auth-ui-card-separator" />

            <div
              v-if="!signInIsDisabled"
              class="field is-grouped is-grouped-centered auth-ui-field--has-margin-top"
            >
              <div class="control auth-ui-has-full-max-width">
                {{ $t("auth.registerSignInLinkDescription") }}
                <router-link
                  :to="signInLink"
                  class="auth-ui-form__link"
                >
                  {{ $t("auth.registerSignInLink") }}
                </router-link>
              </div>
            </div>
            <v-face-book-sign-in
              :button-pressed="buttonPressed"
              :button-text-variable="'auth.fbRegisterButton'"
              :toggle-button-pressed="toggleButtonPressed"
              :show-error-message="showMessage"
            />
          </form>
        </v-card>
      </div>
    </section>
  </div>
</template>

<script>
/* ============
 * Registration Page
 * ============
 *
 * Page where the user can register.
 */

import VCard from "@/components/Card.vue";
import VFaceBookSignIn from "@/components/FacebookSignIn.vue";
import VPasswordRequirements from "@/components/PasswordRequirements.vue";
import Request from "@/tools/Request";
import ValidationTool from "@/tools/ValidationTool";
import QueryStringTool from "../../tools/QueryStringTool";

export default {
  name: "AuthRegister",

  components: {
    VCard,
    VFaceBookSignIn,
    VPasswordRequirements,
  },

  data() {
    return {
      email: "",
      password: "",
      password2: "",
      terms: false,
      buttonPressed: false, // eliminates button spam
      errors: {
        email: false,
        passwordStrength: false,
        passwordsNotMatching: false,
        terms: false,
      },
      termsUrl: process.env.VUE_APP_TERMS_URL,
      serverResponse: {
        message: "",
        visible: false,
        error: false,
      },
      signInIsDisabled: process.env.VUE_APP_SIGN_IN_DISABLED === "true",
      registerIsDisabled: false,
      title: process.env.VUE_APP_PAGE_TITLE,
    };
  },

  computed: {
    registerButtonIsDisabled() {
      const fieldIsEmpty = !(
        this.email.length
        && this.password.length
        && this.password2.length
        && this.terms
      );
      const fieldNotValid = this.errors.email
        || this.errors.passwordStrength
        || this.errors.passwordsNotMatching
        || this.errors.terms;
      return fieldIsEmpty || fieldNotValid || this.registerIsDisabled;
    },

    signInLink() {
      return {
        name: "auth.sign.in",
        query: this.$store.state.route.query,
      };
    },
  },

  mounted() {
    this.setInputFieldsToSubmitFormOnEnterPress();
  },

  methods: {
    clearErrorFromField(fieldName) {
      this.errors[fieldName] = false;
    },

    validatePasswordsMatching() {
      this.errors.passwordsNotMatching = this.password !== this.password2;
    },

    validateEmail() {
      this.errors.email = !ValidationTool.validateEmail(this.email);
    },

    validatePasswordStrength() {
      this.errors.passwordStrength = !ValidationTool.validatePasswordStrength(
        this.password,
      );
    },

    doFormValidations() {
      this.validateEmail();
      this.validatePasswordsMatching();
      this.validatePasswordStrength();
    },

    showMessage(isVisible, message = "", isError = true) {
      this.serverResponse.visible = isVisible;
      this.serverResponse.error = isError;
      this.serverResponse.message = message;
    },

    register() {
      this.doFormValidations();

      if (!this.buttonPressed && !this.registerButtonIsDisabled) {
        this.toggleButtonPressed(true);
        this.toggleShowSpinner(true);
        this.showMessage(false);

        let target = process.env.VUE_APP_AFTER_REGISTER_URL
          ? process.env.VUE_APP_AFTER_REGISTER_URL
          : QueryStringTool.getQueryStringStartFromParam("target");
        target = target !== null ? target : `https://${process.env.VUE_APP_HOST}/`;

        Request.submit("POST", `${process.env.VUE_APP_BACKEND_URL}/register`, {
          email: this.email.toLowerCase(),
          password: this.password,
          terms: this.terms,
          target,
        })
          .then((response) => {
            this.toggleShowSpinner(false);
            if (response.registered) {
              this.registerIsDisabled = true;
              const message = response.emailSent
                ? this.$t("auth.registerSuccessVerifyEmail")
                : this.$t("auth.registerSuccess1")
                  + process.env.VUE_APP_AFTER_REGISTER_URL
                  + this.$t("auth.registerSuccess2");
              this.showMessage(true, message, false);
              // window.location.replace(`${process.env.VUE_APP_AFTER_REGISTER_URL}`);
            } else {
              // This should never happen, as the server returns "registered" in the response,
              // unless the response contains an error which is then handled in the catch block
              this.toggleButtonPressed(false);
              this.showMessage(
                true,
                this.$t("auth.registerServerErrorMessage"),
                true,
              );
            }
          })
          .catch((error) => {
            this.toggleShowSpinner(false);
            this.toggleButtonPressed(false);
            if (error.error.errorCode === "email-invalid") {
              this.showMessage(
                true,
                this.$t("auth.registerErrorMessageEmailInvalid"),
                true,
              );
            } else if (error.error.errorCode === "username-exists") {
              this.showMessage(
                true,
                this.$t("auth.registerErrorMessageEmailInUse"),
                true,
              );
            } else if (error.error.errorCode === "unauthorized") {
              let message = this.$t("auth.errorMessageUnauthorized.default");

              if (
                process.env.VUE_APP_PM_EMAIL
                && process.env.VUE_APP_PM_PHONE_NUMBER
              ) {
                message = this.$t("auth.errorMessageUnauthorized.params", {
                  email: process.env.VUE_APP_PM_EMAIL ?? "",
                  phoneNumber: process.env.VUE_APP_PM_PHONE_NUMBER ?? "",
                });
              }

              this.showMessage(true, message, true);
            } else {
              this.showMessage(
                true,
                this.$t("auth.registerServerErrorMessage"),
                true,
              );
            }
          });
      }
    },

    setInputFieldsToSubmitFormOnEnterPress() {
      const inputs = document.getElementsByTagName("input");

      for (let i = 0; i < inputs.length; i += 1) {
        inputs[i].onkeydown = (e) => {
          if (e.key === "Enter") {
            this.register();
          }
        };
      }
    },

    toggleButtonPressed(isPressed) {
      this.buttonPressed = isPressed;
    },

    /**
     * Toggles the app-wide spinner state in Vuex store.
     */
    toggleShowSpinner(isVisible = true) {
      this.$store.dispatch("general/setShowSpinner", isVisible);
    },
  },
};
</script>
